import { useMemo, useCallback, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
  ErrorMessage,
  LoadingIndicator,
  useToggle,
  Button,
  Modal,
  useBlocker,
} from '../../../../../common';
import {
  GET_ORGANIZATION_WITH_LOCATIONS,
  CHANGE_MUSIC_PROFILE_OF_ZONES,
} from './api';
import Footer from '../../footer';
import LocationCard from './cards/locationCard';
import { ReactComponent as AngleDownIcon } from '../../../../../assets/icons/angleDown.svg';
import { ReactComponent as AngleUpIcon } from '../../../../../assets/icons/angleUp.svg';
import theme from '../../../../../global/style/theme';

const StyledErrorMessage = styled(ErrorMessage)`
  width: 100%;
  margin-top: 2rem;
`;

const StyledLoadingIndicator = styled(LoadingIndicator)`
  margin-top: 2rem;
`;

const StyledForm = styled.form`
  display: grid;
`;

const Container = styled.div`
  height: 100vh;
  display: grid;
  grid-template-rows: 1fr max-content;
`;

const ContentWrapper = styled.div`
  overflow-y: auto;
  padding: 0 1.5rem;
  @media only screen and (min-width: ${({ theme }) =>
      theme.breakpoints.medium}px) {
    padding: 0 2.4rem;
  }
  @media only screen and (min-width: ${({ theme }) =>
      theme.breakpoints.extraLarge}px) {
    padding: 0 4.6rem;
  }
`;

const ModalDescription = styled.p`
  color: ${({ theme }) => theme.dark300};
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 1.4;
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  justify-items: end;
  gap: 1.2rem;
  padding: 2rem 0 1rem 0;
`;

const AddonsMusicManagementTab = () => {
  const { t } = useTranslation();
  const [forceRender, setForceRender] = useState(false);
  const formMethods = useForm();

  const [expandAllLocations, toggleExpandAllLocations] = useToggle(true);
  const [isOpen, setIsOpen] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [isRedirect, setIsRedirect] = useState(false);

  const handleBlockAction = useCallback(
    (action) => {
      // if path is the same as current don't open the modal
      if (formMethods.formState.isSubmitSuccessful) {
        setIsOpen(false);
        setIsRedirect(false);
        action.retry();
        return;
      }

      if (isRedirect) {
        action.retry();
      } else {
        if (action.location.pathname === `/add-ons`) {
          setIsRedirect(false);
          return;
        }
        setRedirectUrl(action.location.pathname);
        setIsOpen(true);
      }
    },
    [formMethods.formState.isSubmitSuccessful, isRedirect]
  );

  const isFieldsDirty = useMemo(() => {
    return (
      formMethods.formState.isDirty ||
      Object.keys(formMethods.formState.dirtyFields).length !== 0
    );
  }, [formMethods.formState.isDirty, formMethods.formState.dirtyFields]);

  useBlocker(handleBlockAction, isFieldsDirty);

  useEffect(() => {
    // Clears the "isDirty" after a successful submit
    if (formMethods.formState.isSubmitted) {
      formMethods.reset(formMethods.getValues());
    }
  }, [
    formMethods.formState.isSubmitted,
    formMethods.reset,
    formMethods.getValues,
    formMethods,
  ]);

  const {
    loading: organizationLoading,
    error: organizationError,
    data: organizationData,
  } = useQuery(GET_ORGANIZATION_WITH_LOCATIONS);

  const allLocations = useMemo(() => {
    if (!organizationData?.organizations) {
      return [];
    }
    return organizationData?.organizations?.reduce((prev, cur) => {
      return [...prev, ...cur.locations];
    }, []);
  }, [organizationData?.organizations]);

  const [
    changeMusicProfileOfZones,
    {
      loading: changeMusicProfileOfZonesLoading,
      error: changeMusicProfileOfZonesError,
    },
  ] = useMutation(CHANGE_MUSIC_PROFILE_OF_ZONES);

  const handleSave = useCallback(
    (values) => {
      // Currently there is no other way then updating all zones from the opened/edited locations
      const zones = [].concat
        .apply([], Object.values(values))
        .filter((zone) => zone?.musicProfile?.value);
      const normalizedEntries = zones.map((changedZone) =>
        changedZone?.musicProfile?.value === '-1'
          ? { zoneId: changedZone.zoneId, musicProfileId: null }
          : {
              zoneId: changedZone.zoneId,
              musicProfileId: changedZone.musicProfile.value,
            }
      );
      changeMusicProfileOfZones({ variables: { entries: normalizedEntries } });
      setIsOpen(false);
      if (redirectUrl) {
        setIsRedirect(true);
      }
    },
    [changeMusicProfileOfZones, redirectUrl]
  );

  const renderLocations = useMemo(() => {
    if (organizationError) {
      return <ErrorMessage error={organizationError} />;
    }
    if (organizationLoading && allLocations.length <= 0) {
      return <StyledLoadingIndicator size="large" thickness="medium" />;
    }
    return allLocations.map((location) => (
      <LocationCard
        key={location.id}
        expandAllLocations={expandAllLocations}
        location={location}
        forceRender={forceRender}
        setForceRender={setForceRender}
      />
    ));
  }, [
    allLocations,
    forceRender,
    organizationError,
    organizationLoading,
    expandAllLocations,
  ]);

  const handleCancel = useCallback(() => {
    formMethods.reset();
    setForceRender(true);
    setIsOpen(false);
    setIsRedirect(true);
  }, [formMethods]);

  if (isRedirect) {
    return <Navigate to={redirectUrl ?? `/add-ons`} />;
  }

  return (
    <FormProvider {...formMethods}>
      <StyledForm>
        <Container>
          <ContentWrapper>
            {renderLocations.length !== 0 && (
              <ButtonWrapper>
                <Button
                  emphasis="secondary"
                  size="small"
                  onClick={toggleExpandAllLocations}
                >
                  {expandAllLocations
                    ? t('addons:thHideAllAddons')
                    : t('addons:thShowAllAddons')}
                </Button>
                {expandAllLocations ? (
                  <AngleUpIcon color={theme.dark300} />
                ) : (
                  <AngleDownIcon color={theme.dark300} />
                )}
              </ButtonWrapper>
            )}
            {renderLocations}
          </ContentWrapper>
          {changeMusicProfileOfZonesError && (
            <StyledErrorMessage error={changeMusicProfileOfZonesError} />
          )}
        </Container>
        {isFieldsDirty && (
          <Footer
            isSubmitting={changeMusicProfileOfZonesLoading}
            onCancel={handleCancel}
            onCancelDisabled={!isFieldsDirty}
            onSave={formMethods.handleSubmit(handleSave)}
            onSaveDisabled={!isFieldsDirty}
          />
        )}
      </StyledForm>
      <Modal
        isOpen={isOpen}
        hideHeader
        onConfirm={formMethods.handleSubmit(handleSave)}
        onCancel={handleCancel}
        requestLoading={changeMusicProfileOfZonesLoading}
      >
        <ModalDescription>{t('addons:changes')}</ModalDescription>
      </Modal>
    </FormProvider>
  );
};

export default AddonsMusicManagementTab;
